import { Dispatch, RefObject, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import useResizeObserver from 'use-resize-observer';

import { CoreLaneRowConfiguration } from '../components/CoreLane/CoreLaneRows';
import { guardSvgElementDimension } from '../helpers/guard-svg-element-dimension';
import { clamp } from '../helpers/mathFunctions';
import { calculateViewportVisibleCoreRange, getLaneFromPosition } from '../helpers/viewportFunctions';
import { FULL_RANGE_RULER_WIDTH_PX, MINIMUM_INITIAL_PX_M } from '../types/constants';
import { ImageTypeLoaders } from '../types/imageTypeLoader';
import { ImageTypeMetadata, LaneThickness } from '../types/imageTypeMetadata';
import { LengthRange } from '../types/lengthRange';

import { laneRowSizeProvider, LaneThicknessProvider, useMergedImageTypeLoaders } from './use-merged-image-type-loaders';
import useVisibleCoreLanes from './use-visible-core-lanes';

/**
 * Given the current core size, and the viewport, what is the maximum zoom scale and range available to fill it with
 * an optional remainder at the end to fill out the last row.
 *
 * Given the following constants;
 * w = width of the viewer in px
 * h = height of the viewer in px
 * c = core image thickness in m (if multiple image types, pre-multiply by the number of image types)
 * f = fixed thickness added to each lane (such as for a horizontal ruler) (in px)
 * L = core length in m
 *
 * And the following unknowns/variables;
 * n = number of rows ∈ N = {1, 2, ...} (i.e. must be +ve integer)
 * s = viewer resolution (px/m)
 * r = "remainder" of the last row (m)
 *
 * Visually represented like so:
 *     ←    w   →
 * 1   [LLLLLLLL] ↑
 * ...
 * n-1 [LLLLLLLL] h
 * n   [LLLLrrrr] ↓
 *
 * We can build a system of equations
 * 1. w * h = (c * s + f) * s * (L + r)	- Every pixel in the viewer must be occupied by the core image, and an optional remainder
 * 2. h = n * (c * s + f)								- The height must be equal to the number of lanes stacked on top of each other
 * 3. w = s * (L + r) / n 							- The total DH length + remainder must fit for all lanes exactly across the viewport
 *
 * With a solution for r and s of:
 * r = (n (c n w + L f) - L h)/(h - f n)
 * s = (h - f n)/(c n)
 * and h!=f n and c n!=0
 *
 * Thanks to Wolfram Alpha (https://www.wolframalpha.com/), we can write this as code for a solver:
 * Solve[{w*h == (L + r) (f + c*s) s, h == n (f + c*s), w == ((L + r) s)/n}, {r, s}]
 *
 * We need to know our number of rows first, as this will be our determining factor,	 *
 * and once again can solve for one of our variables such that the remainder is zero
 * (we require a remainder such that 0 <= r)
 * Solve[0 == (-(h l) + n (f l + c n w))/(h - f n), {n}]
 *
 * Which gives us a solution of:
 *
 * n = (sqrt(l) sqrt(4 c h w + f^2 l) - f l)/(2 c w)
 *
 * (i.e: if we find that x = 12.3, then 12 rows will not fill, but 13 will with a remainder)
 */
type MaximumWindowFit = { scale: number; remainder: number; range: LengthRange } | undefined;

function calculateMaximumWindowFitForLaneThickness(
	range: LengthRange,
	thickness: LaneThickness,
	window: [number, number],
): MaximumWindowFit | undefined {
	const [width, height] = window;
	// No viewport, no limits!
	if (!width || !height) return undefined;
	if (!range) return undefined;
	if (!thickness) return undefined;
	if (!thickness.variableM && !thickness.fixedPx) return undefined;

	/**
	 * As the start/end is not important, just the overall length, then calculate the total core to render
	 */
	const totalCoreLength = range.depthTo - range.depthFrom;
	const variableWidth = thickness.variableM ?? 0;
	const fixedWidth = thickness.fixedPx ?? 0;
	/**
	 * Solve for our function, to get the total number of lanes required to render all core
	 */
	const totalLanes = Math.ceil(
		(Math.sqrt(totalCoreLength) *
			Math.sqrt(4 * variableWidth * height * width + Math.pow(fixedWidth, 2) * totalCoreLength) -
			fixedWidth * totalCoreLength) /
			(2 * variableWidth * width),
	);

	/**
	 * As our lane count is an integer above the actual solution (rounded up) then there is going to be a remainder
	 */
	const remainder =
		(totalLanes * (variableWidth * totalLanes * width + totalCoreLength * fixedWidth) - totalCoreLength * height) /
		(height - fixedWidth * totalLanes);

	/**
	 * Which allows us to then calculate the scale factor
	 */
	const scale = (height - fixedWidth * totalLanes) / (variableWidth * totalLanes);

	return {
		scale,
		remainder,
		range: {
			depthFrom: range.depthFrom,
			depthTo: range.depthTo + remainder,
		},
	};
}

/**
 * The above function finds a fit for a single range and scale.
 * This function applies across all rows, and keeps applying the previous scale to a new round of calculations to re-run in the event of one or more rows crossing visibility threshold
 */
function calculateMaximumWindowFitForLaneThicknessProvider(
	range: LengthRange,
	thickness: LaneThicknessProvider,
	window: [number, number],
): MaximumWindowFit {
	let currFit: MaximumWindowFit = {
		range,
		remainder: 0,
		scale: 0,
	};
	let prevFit: MaximumWindowFit = {
		range,
		remainder: 0,
		scale: 0,
	};

	do {
		// Assign previous scales
		prevFit = currFit;
		const scale = currFit.scale;
		const thicknesses = thickness(scale);
		currFit = thicknesses
			.map((t) => calculateMaximumWindowFitForLaneThickness(range, t, window))
			.filter((f) => Boolean(f))
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			.sort((a, b) => a!.scale - b!.scale)
			.find(() => true);
		if (currFit == undefined) break;
	} while (!prevFit || prevFit.scale < currFit.scale);

	return prevFit;
}

/**
 * The viewport is the container for all the lanes that are being shown.
 */
export interface Viewport {
	/**
	 * Range of the downhole image being shown. Note that this is a continuous one-dimensional range value, but will be translated into two-dimensional lanes
	 */
	range: LengthRange;
	/**
	 * Scale that the viewport is being rendered in (px/m)
	 */
	scale: number;
	/**
	 * Horizontal offset for the first lane, such that this value (in m) will place a gap before the first lane image
	 */
	horizontalOffset: number;
}

// A utility function for calculating the wrapped viewport when the offset length exceeds a full lane
const wrapHorizontalOffset = (prevState: Viewport, width: number): Viewport => {
	const { range, horizontalOffset, scale } = prevState;
	const rowWidth = width / scale;
	if (!prevState) return prevState;
	if (horizontalOffset > rowWidth) {
		const newOffset = horizontalOffset - rowWidth;

		return {
			...prevState,
			range: { ...range },
			scale: scale,
			horizontalOffset: newOffset,
		};
	}
	if (horizontalOffset * -1 > 0) {
		const newOffset = horizontalOffset + rowWidth;

		const newRange = {
			depthFrom: range.depthFrom,
			depthTo: range.depthTo,
		};
		return {
			...prevState,
			range: newRange,
			scale: scale,
			horizontalOffset: newOffset,
		};
	}
	return prevState;
};

/**
 * Using some viewport parameters, calculate the end of lanes, if the final lane is only partial
 * @param {LengthRange} length of the drilled core to calculate for
 * @param {number} horizontalOffset horizontal offset of the first lane
 * @param {number} laneLength length of each lane in meters
 * @returns {LengthRange} a range whereby the start and end are both expanded such that partial rows are included
 *
 * @desc
 * Consider the following lane layout, with lane labels (A, B C), and Drilled Core Range {from: S, to: E};
 * X
 * |
 * [   S#######] A
 * [###########] B
 * [#######E   ] C
 * 						|
 * 						Y
 *
 * and a Lane horizontal offset from X to S in Lane A
 *
 * THEN the output will provide a range which encompasses everything, {from: X, to: Y}
 *
 */
const calculateExpandedLaneBounds = (
	length: LengthRange,
	horizontalOffset: number,
	laneLength: number,
): LengthRange => {
	const { depthFrom, depthTo } = length;
	// If zero length, just spit out as we have not yet fully loaded
	if (depthTo - depthFrom === 0) return length;

	let lanesTo = depthFrom;
	while (lanesTo <= depthTo) {
		lanesTo += laneLength;
	}
	const remainder = lanesTo - depthTo;
	if (horizontalOffset > remainder) lanesTo += laneLength;

	/* 	TODO:
			This function only extends the end range, as the correction for the horizontal offset is done by the callee.
	* 	We will need to move the calculation for that compensation to this function at a later date to consolidate how the lanes are aligned
	*/
	return { depthFrom: depthFrom - horizontalOffset, depthTo: lanesTo - horizontalOffset };
};

export interface XYPosition {
	x: number;
	y: number;
}

export interface ViewDimensions {
	viewerRef: RefObject<HTMLDivElement>;
	viewPortRef: RefObject<SVGSVGElement>;
	viewerWidth: number;
	viewerHeight: number;
}

const useViewDimensions = (): ViewDimensions => {
	const viewerRef = useRef<HTMLDivElement>(null);
	const viewPortRef = useRef<SVGSVGElement>(null);
	const viewerDimensionsObservable = useResizeObserver({ ref: viewerRef });
	const { width: viewerWidth, height: viewerHeight } = viewerDimensionsObservable;

	return {
		viewerRef,
		viewPortRef,
		viewerWidth: guardSvgElementDimension(viewerWidth ?? 0) - FULL_RANGE_RULER_WIDTH_PX,
		viewerHeight: guardSvgElementDimension(viewerHeight ?? 0),
	};
};

export interface DynamicViewport {
	/** data for maintaining details about the viewport size */
	viewDimensions: ViewDimensions;

	/** zoom out all the way to fit the entire image in the view */
	fitToWindow: () => void;
	/** the zoom level up to 1.0, representing the ratio of the highest scree/pixel ratio of the highest resolution image */
	zoom: number;
	/** set the zoom level */
	setZoom: (zoom: number) => void;
	/**
	 * Delegate for zooming in and out using the mouse
	 * @param {number} delta the unitless value for mouse zoom velocity
	 */
	zoomHandler: (delta: number, viewportPosition?: XYPosition) => void;
	/**
	 * Delegate for panning the viewport around (horizontal and vertical)
	 * @param {{x: number, y: number}} delta the drag value (in px) left and right
	 */
	panHandler: (delta: { x: number; y: number }) => void;
	/**
	 * Range of the downhole image being shown. Note that this is a continuous one-dimensional range value, but will be translated into two-dimensional lanes
	 */
	range: LengthRange;
	/**
	 * Set the visible range, altering the scale and offset to fit
	 * @param {LengthRange} range
	 */
	setRange: (range: LengthRange) => void;
	/**
	 * Set the to range to best fit visible rows, starting exactly at the very beginning of the range and allowing a remainder if necessary
	 * @param {LengthRange} range
	 */
	fitRangeToFullRows: (range: LengthRange) => void;
	/**
	 * Scale that the viewport is being rendered in (px/m)
	 */
	scale: number;
	/**
	 * Set the scale, centered on either the viewport, or hovered row, altering the range, and offset to fit
	 * @param {number} scale Scale in px/m
	 */
	setScale: (scale: number) => void;
	/**
	 * Height of the horizontal ruler (if shown)
	 */
	horizontalRulerHeight?: number;
	/**
	 * Horizontal offset for the first lane, such that this value (in m) will place a gap before the first lane image
	 */
	horizontalOffset: number;

	/** The hovered lane range */
	hoverLaneRange: LengthRange | undefined;

	/** Set the hovered lane range for the context, making it available to every core viewer window */
	setHoverLaneRange: Dispatch<SetStateAction<LengthRange | undefined>>;

	/** Broadcast current loading percentage of the image */
	onLoadingPercentageChanged: (loadingPercentage: number) => void;
	/** Add a handler for loading percentage changes */
	addLoadingPercentageChangedHandler: (key: string, handler: (loadingPercentage: number | undefined) => void) => void;
	/** Remove a handler for loading percentage changes */
	removeLoadingPercentageChangedHandler: (key: string) => void;
	onOverviewLoaded: (isLoaded: boolean) => void;
	addOverviewLoadingChangedHandler: (key: string, handler: (isLoaded: boolean) => void) => void;
	removeOverviewLoadingChangedHandler: (key: string) => void;

	/** When multiple viewer panes are shown, created a superset of the range for each such that all configurations will fall within the from and to **/
	mergedRange: LengthRange | undefined;
	/** When multiple viewer panes are shown, these are the thicknesses for each pane row, optionally with a scale factor to get the thicknesses (for things like show/hide) **/
	mergedThicknessProvider: LaneThicknessProvider;

	/** The visible cores to be rendered based on the current viewport */
	visibleCoreLanesRanges: LengthRange[];

	/** The completely visible range of core including partially visible lanes - this can be beyond the bounds of the actual range due to the zoom and pan algorithm.
	 * Important for loading images on zoom */
	completelyVisibleLanesRange: LengthRange;

	/** The range of core visible to the user within a certain amount of tolerance. This means that a lane needs to be at least 10% visible to be included */
	viewportVisibleLanesRange: LengthRange;

	/** Get the Y location of a lane */
	getLaneYLocation: (lane: LengthRange) => number | undefined;

	/** the current height of a lane based on the merged dimensions and the current scale */
	laneHeight: number;
}

export interface DynamicViewportProps {
	/**
	 * Opaque key whose change triggers a viewport reset. The consumer assigns whatever string
	 * uniquely identifies the data source being viewed (e.g. a stringified drillhole) — when it
	 * differs from the previously-initialised value, the viewport is re-fitted to the new data.
	 * CV itself doesn't interpret the value.
	 */
	viewportResetKey?: string;
	getOrLoadImageType: (imageTypeId: number) => ImageTypeMetadata | undefined;
	loaders: ImageTypeLoaders;
	/**
	 * Event fired when the viewport range changes
	 * @param range The new range
	 */
	onRangeChanged?: (range: LengthRange, viewportVisibleRange: LengthRange) => void;
	configurations: CoreLaneRowConfiguration[][];
}

export const calculateHeightInPxForLaneThickness = (thickness: LaneThickness | undefined, scale: number): number => {
	if (thickness?.threshold && scale <= thickness?.threshold) return 0;
	return (thickness?.fixedPx ?? 0) + (thickness?.variableM ?? 0) * scale;
};

export const useDynamicViewport = ({
	viewportResetKey,
	loaders,
	onRangeChanged,
	configurations,
}: DynamicViewportProps): DynamicViewport => {
	const { mergedRange, mergedLaneThickness, highestAvailableResolution } = useMergedImageTypeLoaders(
		loaders,
		configurations,
	);

	const viewDimensions = useViewDimensions();
	const { viewerWidth, viewerHeight } = viewDimensions;

	const [hoverLaneRange, setHoverLaneRange] = useState<LengthRange>();

	const devicePixelRatio = window.devicePixelRatio;
	// This is not correct for high dpi screens, projectors/ etc.
	// Scale is in px/m
	const zoomFactor = highestAvailableResolution / devicePixelRatio;
	/**
	 * Method for calculating the best fit scale for a visible range given a known viewport size.
	 * This calculation does not enforce fully visible rows
	 * @type {(range: LengthRange) => number}
	 */
	// TODO needs to account for variable row sizes, much like the fit window to range
	const calculateFitScaleForRange = useCallback(
		(range: LengthRange, thickness: LaneThickness): number => {
			// Check if scale exceeds the threshold
			if (!viewerWidth || !viewerHeight) return MINIMUM_INITIAL_PX_M;
			const totalAreaInPxSq = (viewerWidth - FULL_RANGE_RULER_WIDTH_PX) * viewerHeight;

			const coreLengthInM = range.depthTo - range.depthFrom;
			const coreWidthInM = thickness.variableM ?? 0;

			const coreAreaInMSq = coreLengthInM * coreWidthInM;

			return Math.sqrt(totalAreaInPxSq) / Math.sqrt(coreAreaInMSq);
		},
		[viewerWidth, viewerHeight],
	);

	const [viewport, setViewport] = useState<Viewport>({
		horizontalOffset: 0,
		scale: 0,
		range: { depthFrom: 0, depthTo: 0 },
	});

	const { visibleCoreLanesRanges, completelyVisibleLanesRange, viewportVisibleLanesRange } = useVisibleCoreLanes({
		viewPortWidth: viewerWidth,
		viewPortHeight: viewerHeight,
		viewPortResolution: viewport.scale,
		viewPortDrilledCoreRange: viewport.range,
		visualDrillHoleStartOffset: viewport.horizontalOffset,
		totalDrilledCoreRange: mergedRange ?? { depthFrom: 0, depthTo: 1 },
	});

	// const laneRulerHeight = viewport.scale > LANE_RULER_SCALE_THRESHOLD ? LANE_RULER_HEIGHT_PX : 0;

	// Using the current scale, calculate the lane height across all merged lane groups
	const laneHeight = useMemo(() => {
		const configHeights = configurations.map(
			(l) =>
				l.reduce(
					(prev, curr) =>
						prev +
						calculateHeightInPxForLaneThickness(
							laneRowSizeProvider(curr, loaders, viewport.scale)?.thickness,
							viewport.scale,
						),
					0,
				) ?? [],
		);
		return Math.max(...configHeights);
	}, [configurations, loaders, viewport.scale]);

	const getLaneYLocation = useCallback(
		(lane: LengthRange) => {
			if (!viewerWidth) return;
			if (!viewport.range) return;

			const positionIndex = (lane.depthFrom - viewport.range.depthFrom) / (viewerWidth / viewport.scale);

			const result = positionIndex * laneHeight;

			return Number.isNaN(result) ? 0 : result;
		},
		[viewerWidth, viewport.range, viewport.scale, laneHeight],
	);

	const updateRange = useCallback(
		(range: LengthRange, scale: number, horizontalOffset: number) => {
			const viewportVisible = calculateViewportVisibleCoreRange(
				range ?? { depthFrom: 0, depthTo: 1 },
				scale,
				viewerWidth,
				viewerHeight,
				range,
				horizontalOffset,
			);
			onRangeChanged?.(range, viewportVisible);
		},
		[onRangeChanged, viewerHeight, viewerWidth],
	);

	// Late-subscriber-safe pub-sub for loading state. Without retaining the latest value,
	// a subscriber that registers after the producer has already fired never sees the event —
	// since overview chunks now land synchronously (no per-column encoding), this race is easy
	// to lose, leaving the StatusBar stuck on "Loading…" indefinitely.
	const loadingPercentageChangedHandlers = useRef<Record<string, (loadingPercentage: number | undefined) => void>>(
		{},
	);
	const lastLoadingPercentage = useRef<number | undefined>(undefined);

	const addLoadingPercentageChangedHandler = useCallback(
		(key: string, handler: (loadingPercentage: number | undefined) => void) => {
			loadingPercentageChangedHandlers.current[key] = handler;
			handler(lastLoadingPercentage.current);
		},
		[],
	);
	const removeLoadingPercentageChangedHandler = useCallback((key: string) => {
		delete loadingPercentageChangedHandlers.current[key];
	}, []);

	const onLoadingPercentageChanged = useCallback((loadingPercentage: number) => {
		lastLoadingPercentage.current = loadingPercentage;
		Object.values(loadingPercentageChangedHandlers.current).forEach((handler) => handler(loadingPercentage));
	}, []);

	const overviewLoadedChangedHandlers = useRef<Record<string, (isLoaded: boolean) => void>>({});
	const lastOverviewLoaded = useRef<boolean>(false);

	const addOverviewLoadingChangedHandler = useCallback((key: string, handler: (isLoaded: boolean) => void) => {
		overviewLoadedChangedHandlers.current[key] = handler;
		handler(lastOverviewLoaded.current);
	}, []);
	const removeOverviewLoadingChangedHandler = useCallback((key: string) => {
		delete overviewLoadedChangedHandlers.current[key];
	}, []);

	const onOverviewLoaded = useCallback((isLoaded: boolean) => {
		lastOverviewLoaded.current = isLoaded;
		Object.values(overviewLoadedChangedHandlers.current).forEach((handler) => handler(isLoaded));
	}, []);

	/**
	 * On initial load, set the view to the full drill hole range
	 */
	const [initialised, setInitialised] = useState<string>();

	const panHandler = useCallback(
		(delta: { x: number; y: number }) => {
			if (!viewerHeight) return;
			if (!viewerWidth) return;
			if (!mergedRange) return;

			const { x: xDelta, y: yDelta } = delta;

			// Calculate the delta such that if we hit a DH boundary, we limit to that
			// When panning, this is the modifier for mouse input delta and actual viewport movement
			const calcDelta = (15 / viewport.scale) * yDelta;
			setViewport((prevState) => {
				if (!prevState) return prevState;

				// Take into consideration whether row rulers are drawn or not when recalculating range
				const { scale } = prevState;
				const laneLength = viewerWidth / scale;
				const laneThickness = Math.max(
					...(mergedLaneThickness(scale).map((t) => calculateHeightInPxForLaneThickness(t, viewport.scale)) ??
						[]),
				);
				const numberOfLanes = viewerHeight / laneThickness;
				const newViewableSize = numberOfLanes * laneLength;

				const horizontalOffset = prevState.horizontalOffset + xDelta / prevState.scale;

				const laneBounds = calculateExpandedLaneBounds(mergedRange, horizontalOffset, viewerWidth / scale);

				let internalDelta = calcDelta;
				if (prevState.range.depthFrom + calcDelta < laneBounds.depthFrom)
					internalDelta = laneBounds.depthFrom - prevState.range.depthFrom;
				if (prevState.range.depthTo + calcDelta > laneBounds.depthTo)
					internalDelta = laneBounds.depthTo - prevState.range.depthTo;

				const newViewableBounds = {
					depthFrom: Math.max(prevState.range.depthFrom + internalDelta, laneBounds.depthFrom),
					depthTo: Math.min(prevState.range.depthFrom + internalDelta + newViewableSize, laneBounds.depthTo),
				};

				updateRange(newViewableBounds, viewport.scale, horizontalOffset);
				return wrapHorizontalOffset(
					{
						...prevState,
						horizontalOffset,
						range: newViewableBounds,
					},
					viewerWidth,
				);
			});
		},
		[viewerHeight, viewerWidth, mergedLaneThickness, mergedRange, viewport.scale, updateRange],
	);

	/**
	 * Callback for setting a new scale value for the viewport, while retaining, or calculating the other viewport variables
	 */
	const setScale = useCallback(
		/**
		 * Set the scale, can be absolute or relative to previous scale
		 * @param {(previousScale: number) => number} setFn set scale function
		 * @param viewportPosition
		 */
		(setFn: (previousScale: number) => number, viewportPosition?: XYPosition): void => {
			// No viewport no scale!
			if (!viewerHeight || !viewerWidth) return;
			if (!mergedRange) return;
			if (!mergedLaneThickness) return;

			const minFit = calculateMaximumWindowFitForLaneThicknessProvider(mergedRange, mergedLaneThickness, [
				viewerWidth,
				viewerHeight,
			]);

			/*
			 * Compute the new viewport parameters. Although we are only applying a new scale factor,
			 * we need to alter the other 2 viewport in unison such that the bounds of our
			 * viewport and drillhole are not exceeded
			 */
			setViewport((prevState) => {
				const { range, scale } = prevState;
				// We can only zoom on an already rendered viewport
				if (!prevState || !range) return prevState;

				// New scale factor, calculated by the input delegate set function
				const newScale = setFn(scale);

				// The new length of a lane, given our updated zoom level
				const newLaneLength = viewerWidth / newScale;

				// If we are zooming out, but at the allowable limits of our zooming, then the viewport will not update at all
				// TODO check using the row range function as used in the pan function

				if (!minFit) return prevState;

				const { scale: minScale, range: minRange } = minFit;

				if (newScale < minScale) {
					updateRange(minRange, minScale, 0);
					return {
						range: minRange,
						horizontalOffset: 0,
						scale: minScale,
					};
				}

				// The length on the drilled core length where the lane midpoint lies
				// The ratio of the viewport range which constitutes the first half of our hover midpoint
				let viewPortMidPointRatio = 0.5;
				let leftAmount = 0.5;

				if (viewportPosition) {
					//calculate how far along the lane we are - in order to zoom proportionally left and right
					leftAmount = Math.max(0, Math.min(1, (viewPortMidPointRatio = viewportPosition.x / viewerWidth)));

					const hoveredLane = getLaneFromPosition(
						visibleCoreLanesRanges,
						getLaneYLocation,
						laneHeight,
						viewportPosition.y,
					);

					// If we are hovering on a lane, it should be the epicentre of our zoom
					if (hoveredLane) {
						// the length from the start of the lane which the midpoint lies
						const hoverRowMidpoint = (hoveredLane.depthTo - hoveredLane.depthFrom) * leftAmount;
						// Calculate the start of the new midpoint row, given our zoom has been applied
						const hoverRowMidLength =
							hoveredLane.depthFrom + hoverRowMidpoint + newLaneLength * (1 - leftAmount);
						// Recalculate the VP length midpoint target
						viewPortMidPointRatio =
							1 - (range.depthTo - hoverRowMidLength) / (range.depthTo - range.depthFrom);
					}
				}

				// The size of the viewport range in m
				const viewportSize = range.depthTo - range.depthFrom;

				const drillHoleMidPoint = range.depthFrom + viewportSize * viewPortMidPointRatio;

				const laneThickness = Math.max(
					...(mergedLaneThickness(newScale)?.map((t) => calculateHeightInPxForLaneThickness(t, newScale)) ??
						[]),
				);

				const rows = viewerHeight / laneThickness;
				const newViewableSize = rows * newLaneLength;

				const newCalculatedFrom = drillHoleMidPoint - newViewableSize * viewPortMidPointRatio;
				const newCalculatedTo = drillHoleMidPoint + newViewableSize * (1 - viewPortMidPointRatio);

				const startClipped = newCalculatedFrom < mergedRange.depthFrom;
				const endClipped = newCalculatedTo > mergedRange.depthTo;
				const zoomedToMax = startClipped && endClipped;
				const startClippedOffset = mergedRange.depthFrom - newCalculatedFrom;
				const endClippedOffset = newCalculatedTo - mergedRange.depthTo;

				// If either the start or end was clipped, add the offset to the other end.
				// - The zoom scale expects a given length range (newViewableSize) to fill the entire screen.
				const newViewableBounds = {
					depthFrom: Math.max(
						newCalculatedFrom - (endClipped && !zoomedToMax ? endClippedOffset : 0),
						minRange.depthFrom,
					),
					depthTo: Math.min(
						newCalculatedTo + (startClipped && !zoomedToMax ? startClippedOffset : 0),
						minRange.depthTo,
					),
				};

				let remainder = drillHoleMidPoint;
				while (remainder > mergedRange.depthFrom) remainder -= newLaneLength;
				if (
					newViewableBounds.depthFrom <= minRange.depthFrom &&
					newViewableBounds.depthTo >= minRange.depthTo
				) {
					updateRange(minRange, minScale, 0);
					return {
						range: minRange,
						scale: minScale,
						horizontalOffset: 0,
					};
				}

				updateRange(newViewableBounds, newScale, mergedRange.depthFrom - remainder);
				return {
					horizontalOffset: mergedRange.depthFrom - remainder,
					scale: newScale,
					range: newViewableBounds,
				};
			});
		},
		[
			viewerHeight,
			viewerWidth,
			mergedRange,
			mergedLaneThickness,
			updateRange,
			visibleCoreLanesRanges,
			getLaneYLocation,
			laneHeight,
		],
	);

	const fitToWindow = useCallback(() => {
		setScale(() => 1);
	}, [setScale]);

	const setZoom = useCallback(
		(zoom: number) => {
			setScale(() => zoom * zoomFactor);
		},
		[zoomFactor, setScale],
	);

	/**
	 * Handle a mouse zoom event
	 */
	const zoomHandler = useCallback(
		(delta: number, viewportPosition?: XYPosition) => {
			const zoomFactor = 0.6;
			setScale(
				(prevState) => clamp(prevState + zoomFactor * delta * -1, 1, highestAvailableResolution * 2), //max zoom == 200%
				viewportPosition,
			);
		},
		[highestAvailableResolution, setScale],
	);

	const resetViewport = useCallback(() => {
		if (!viewerWidth || !viewerHeight) return;
		if (!mergedRange) return;

		const minFit = calculateMaximumWindowFitForLaneThicknessProvider(mergedRange, mergedLaneThickness, [
			viewerWidth,
			viewerHeight,
		]);

		if (!minFit) return;
		const { scale: minScale, range: minRange } = minFit;
		updateRange(minRange, minScale, 0);
		setViewport({ scale: minScale, range: minRange, horizontalOffset: 0 });
		return;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [viewerWidth, viewerHeight, mergedRange, updateRange]);

	/**
	 * When the viewportResetKey changes, reset the VP. It is re-initialised below.
	 */
	useEffect(() => {
		setInitialised(undefined);
	}, [viewportResetKey]);

	/**
	 * When loading in a new image type to the viewer, it is reset (see above), and this effect will re-initialise for the current viewportResetKey.
	 */
	useEffect(() => {
		// If Viewer is not in a ready state, we skip
		if (!viewerWidth || !viewerHeight) return;
		if (!viewportResetKey) return;
		if (!mergedRange) return;

		// If the already-initialised viewportResetKey matches AND the viewport's range is sensible
		// relative to the current `mergedRange`, we can skip. The extra checks guard against a race
		// where `setInitialised(viewportResetKey)` ran before the new data source's metadata loaded
		// — at that point `mergedRange` was still the previous source's, so `resetViewport()` set
		// the viewport to a range that doesn't fit the new source (often degenerate, e.g.
		// depthFrom === depthTo when the previous source had a tiny range). When `mergedRange`
		// later settles, this effect re-fires (resetViewport's identity depends on mergedRange),
		// and without this fall-through the early-exit would leave the viewport stuck on the stale
		// range — the lane SVGs end up zero-width and nothing renders.
		if (
			initialised &&
			viewportResetKey === initialised &&
			mergedRange &&
			viewport.range.depthFrom < viewport.range.depthTo &&
			viewport.range.depthFrom >= mergedRange.depthFrom &&
			viewport.range.depthTo <= mergedRange.depthTo
		) {
			return;
		}
		const minFit = calculateMaximumWindowFitForLaneThicknessProvider(mergedRange, mergedLaneThickness, [
			viewerWidth,
			viewerHeight,
		]);

		if (!minFit) return;
		const range = viewport.range;
		const { range: imageRange } = minFit;
		if (initialised === undefined) {
			resetViewport();
		} else if (!range.depthFrom && !range.depthTo) {
			resetViewport();
		} else if (range.depthFrom < imageRange.depthFrom || range.depthTo > imageRange.depthTo) {
			resetViewport();
		} else {
			setViewport((vp) => ({
				...vp,
				scale: Math.min(...mergedLaneThickness(vp.scale).map((t) => calculateFitScaleForRange(vp.range, t))),
			}));
		}
		// Only load if all image type initialisers are complete
		if (Object.keys(loaders).length && !Object.values(loaders).filter((il) => il.isLoading).length)
			setInitialised(viewportResetKey);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialised, resetViewport, viewportResetKey]);

	/**
	 * Make sure that any height/width changes ensure that the VP is valid by either reducing
	 * the viewer range to fit the new bounds, or in the case it is larger than the DH, expanding it
	 */
	useEffect(() => {
		if (!viewerWidth || !viewerHeight) return;
		if (!mergedRange) return;

		const minFit = calculateMaximumWindowFitForLaneThicknessProvider(mergedRange, mergedLaneThickness, [
			viewerWidth,
			viewerHeight,
		]);

		if (!minFit) return;
		const { scale: minScale, range: minRange } = minFit;
		if (!viewerWidth) return;
		if (!viewerHeight) return;
		setViewport((prevState) => {
			const laneThicknessInPx = Math.max(
				...(mergedLaneThickness(minScale)?.map((t) =>
					calculateHeightInPxForLaneThickness(t, prevState.scale),
				) ?? []),
			);

			// Calculate the new CP length
			const newLength = (viewerWidth / prevState.scale) * (viewerHeight / laneThicknessInPx);

			if (!prevState) return prevState;

			// Calculate how much the new size changes by
			const difference = newLength - (prevState.range.depthTo - prevState.range.depthFrom);

			// If we expand the VP, then it grows/shrinks from the bottom
			const newRange = {
				depthFrom: prevState.range.depthFrom,
				depthTo: prevState.range.depthTo + difference,
			};

			// If the new range being displayed is bigger than our allowable VP, then clamp it
			if (newRange.depthFrom < minRange.depthFrom || newRange.depthTo > minRange.depthTo) {
				updateRange(minRange, minScale, 0);
				return {
					range: minRange,
					scale: minScale,
					horizontalOffset: 0,
				};
			}

			updateRange(newRange, prevState.scale, prevState.horizontalOffset);
			return {
				...prevState,
				range: newRange,
			};
		});
		// Only trigger on height and/or width changes
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [viewerHeight, viewerWidth]);

	/**
	 * Callback for setting the current viewport to a length range
	 */
	const setViewportRange = useCallback(
		/**
		 * When updating the range, set to a defined window
		 * @param {LengthRange} range the range to set to
		 */
		(range: LengthRange) => {
			// No window now range!
			if (!viewerWidth) return;
			if (!viewerHeight) return;
			if (!mergedRange) return;
			// Go ahead and set the viewport to the right range
			// TODO: Recalculate the scale, and the offset to accomodate the range
			setViewport((s) => {
				if (range.depthFrom === s.range.depthFrom && range.depthTo === s.range.depthTo) return s;

				// Sanitise external range so that calculations don't break internal assumptions
				const guardedFrom = Math.max(Math.min(range.depthFrom, mergedRange.depthTo), mergedRange.depthFrom);
				const guardedTo = Math.min(Math.max(range.depthTo, guardedFrom + 0.01), mergedRange.depthTo);

				const guardedRange = {
					depthFrom: guardedFrom,
					depthTo: guardedTo,
				};

				const newRange = {
					...s,
					range: guardedRange,
					scale: Math.min(
						...mergedLaneThickness(s.scale).map((t) => calculateFitScaleForRange(guardedRange, t)),
					),
				};
				updateRange(newRange.range, newRange.scale, newRange.horizontalOffset);
				return newRange;
			});
		},
		[viewerWidth, viewerHeight, mergedRange, calculateFitScaleForRange, mergedLaneThickness, updateRange],
	);

	const fitRangeToFullRows = useCallback(
		/**
		 * When updating the range, set to a defined window
		 * @param {LengthRange} range the range to set to
		 */
		(range: LengthRange) => {
			// No window now range!
			if (!viewerWidth) return;
			if (!viewerHeight) return;
			if (!mergedRange) return;

			const fit = calculateMaximumWindowFitForLaneThicknessProvider(range, mergedLaneThickness, [
				viewerWidth,
				viewerHeight,
			]);
			if (!fit) return;

			const { scale: fitScale, range: fitRange } = fit;

			const rowLength = viewerWidth / fitScale;

			// Sanitise external range so that calculations don't break internal assumptions
			const guardedFrom = Math.max(Math.min(fitRange.depthFrom, mergedRange.depthTo), mergedRange.depthFrom);
			const guardedTo = Math.min(Math.max(fitRange.depthTo, guardedFrom), mergedRange.depthTo);

			const guardedRange = {
				depthFrom: guardedFrom,
				depthTo: guardedTo,
			};

			let fitRangeStart = guardedRange.depthFrom;
			while (fitRangeStart - rowLength > mergedRange.depthFrom) {
				fitRangeStart -= rowLength;
			}
			setViewport({
				range: fitRange,
				scale: fitScale,
				horizontalOffset: rowLength - (fitRangeStart - mergedRange.depthFrom) + 0.000000000001, //small offset to compensate for visible row check
			});
		},
		[viewerWidth, viewerHeight, mergedRange, mergedLaneThickness],
	);

	return {
		viewDimensions,
		hoverLaneRange,
		setHoverLaneRange,
		fitToWindow,
		zoom: viewport.scale / zoomFactor,
		setZoom,
		zoomHandler,
		panHandler,
		range: viewport.range,
		setRange: setViewportRange,
		fitRangeToFullRows,
		scale: viewport.scale,
		setScale: (s) => setScale(() => s),
		horizontalRulerHeight: 0,
		horizontalOffset: viewport.horizontalOffset,
		addLoadingPercentageChangedHandler,
		removeLoadingPercentageChangedHandler,
		onLoadingPercentageChanged,
		addOverviewLoadingChangedHandler,
		removeOverviewLoadingChangedHandler,
		onOverviewLoaded,
		mergedRange,
		mergedThicknessProvider: mergedLaneThickness,
		visibleCoreLanesRanges,
		completelyVisibleLanesRange,
		viewportVisibleLanesRange,
		getLaneYLocation,
		laneHeight,
	};
};
